/**
 * Summary Prompts Editor Component
 * Allows teachers to customize GPT prompt templates for summary generation
 */

import React, { useState, useEffect } from 'react';
import * as promptsService from '../../services/summaryPromptsService';

const PROMPT_TYPES: Array<'teacher' | 'reader'> = ['teacher', 'reader'];
const GRADE_LEVELS = ['all', 'K-2', '3-5', '6-8', '9-12'];

interface SummaryPromptsEditorProps {
  onClose?: () => void;
}

const SummaryPromptsEditor: React.FC<SummaryPromptsEditorProps> = ({ onClose }) => {
  const [selectedType, setSelectedType] = useState<'teacher' | 'reader'>('teacher');
  const [selectedGrade, setSelectedGrade] = useState<string>('all');
  const [promptTemplate, setPromptTemplate] = useState<string>('');
  const [originalTemplate, setOriginalTemplate] = useState<string>('');
  const [variables, setVariables] = useState<promptsService.PromptVariable[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState<boolean>(false);
  const [version, setVersion] = useState<number>(1);

  // Load prompt template when type or grade changes
  useEffect(() => {
    loadPrompt();
    loadVariables();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedType, selectedGrade]);

  // Track changes
  useEffect(() => {
    setHasChanges(promptTemplate !== originalTemplate);
  }, [promptTemplate, originalTemplate]);

  const loadPrompt = async () => {
    setLoading(true);
    setError(null);
    try {
      const prompt = await promptsService.getPrompt(selectedType, selectedGrade);
      setPromptTemplate(prompt.prompt_template);
      setOriginalTemplate(prompt.prompt_template);
      setVersion(prompt.version);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load prompt');
    } finally {
      setLoading(false);
    }
  };

  const loadVariables = async () => {
    try {
      const vars = await promptsService.getPromptVariables(selectedType);
      setVariables(vars);
    } catch (err) {
      console.error('Failed to load variables:', err);
    }
  };

  const handleSave = async () => {
    // Validate required variables
    const requiredVars = variables
      .filter(v => v.is_required)
      .map(v => v.variable_name);
    
    const validation = promptsService.validatePromptTemplate(promptTemplate, requiredVars);
    
    if (!validation.isValid) {
      setError(
        `Missing required variables: ${validation.missingVariables.map((v: string) => `{${v}}`).join(', ')}`
      );
      return;
    }

    setSaving(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const updated = await promptsService.updatePrompt(selectedType, selectedGrade, {
        prompt_template: promptTemplate,
        notes: `Updated via dashboard on ${new Date().toLocaleString()}`
      });
      
      setOriginalTemplate(updated.prompt_template);
      setVersion(updated.version);
      setSuccessMessage(`Prompt saved successfully! (Version ${updated.version})`);
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save prompt');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Are you sure you want to reset this prompt to the default? This will discard your changes.')) {
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const reset = await promptsService.resetPrompt(selectedType, selectedGrade);
      setPromptTemplate(reset.prompt_template);
      setOriginalTemplate(reset.prompt_template);
      setVersion(reset.version);
      setSuccessMessage('Prompt reset to default successfully!');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reset prompt');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (hasChanges && !window.confirm('You have unsaved changes. Are you sure you want to cancel?')) {
      return;
    }
    setPromptTemplate(originalTemplate);
    setError(null);
    setSuccessMessage(null);
  };

  const insertVariable = (variableName: string) => {
    const textarea = document.getElementById('prompt-textarea') as HTMLTextAreaElement;
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const text = promptTemplate;
      const before = text.substring(0, start);
      const after = text.substring(end);
      const newText = before + `{${variableName}}` + after;
      
      setPromptTemplate(newText);
      
      // Set cursor position after inserted variable
      setTimeout(() => {
        textarea.focus();
        const newPosition = start + variableName.length + 2;
        textarea.setSelectionRange(newPosition, newPosition);
      }, 0);
    }
  };

  const usedVariables = promptsService.extractVariables(promptTemplate);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-7xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-purple-600 to-blue-600">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-white">
              📝 Summary Prompt Editor
            </h2>
            {onClose && (
              <button
                onClick={onClose}
                className="text-white hover:text-gray-200 transition-colors"
              >
                <span className="text-2xl">×</span>
              </button>
            )}
          </div>
          <p className="text-purple-100 mt-1 text-sm">
            Customize GPT prompts for generating reading session summaries
          </p>
        </div>

        {/* Controls */}
        <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
          <div className="flex gap-4 items-center flex-wrap">
            <div className="flex-1 min-w-[200px]">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Summary Type
              </label>
              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value as 'teacher' | 'reader')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                disabled={loading}
              >
                {PROMPT_TYPES.map(type => (
                  <option key={type} value={type}>
                    {type.charAt(0).toUpperCase() + type.slice(1)} Summary
                  </option>
                ))}
              </select>
            </div>

            <div className="flex-1 min-w-[200px]">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Grade Level
              </label>
              <select
                value={selectedGrade}
                onChange={(e) => setSelectedGrade(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                disabled={loading}
              >
                {GRADE_LEVELS.map(grade => (
                  <option key={grade} value={grade}>
                    {grade === 'all' ? 'All Grades (Default)' : `Grade ${grade}`}
                  </option>
                ))}
              </select>
            </div>

            <div className="text-sm text-gray-600 pt-6">
              Version: {version}
            </div>
          </div>
        </div>

        {/* Error/Success Messages */}
        {error && (
          <div className="mx-6 mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
            <div className="flex items-start">
              <span className="text-red-600 mr-2">⚠️</span>
              <span className="text-red-800 text-sm">{error}</span>
            </div>
          </div>
        )}

        {successMessage && (
          <div className="mx-6 mt-4 p-3 bg-green-50 border border-green-200 rounded-md">
            <div className="flex items-start">
              <span className="text-green-600 mr-2">✓</span>
              <span className="text-green-800 text-sm">{successMessage}</span>
            </div>
          </div>
        )}

        {/* Main Content */}
        <div className="flex-1 overflow-hidden flex">
          {/* Editor Panel */}
          <div className="flex-1 p-6 overflow-auto">
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Prompt Template
                {hasChanges && <span className="text-orange-600 ml-2">(Modified)</span>}
              </label>
              {loading ? (
                <div className="flex items-center justify-center h-64 bg-gray-50 rounded-md">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                </div>
              ) : (
                <textarea
                  id="prompt-textarea"
                  value={promptTemplate}
                  onChange={(e) => setPromptTemplate(e.target.value)}
                  className="w-full h-[400px] px-3 py-2 border border-gray-300 rounded-md font-mono text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
                  placeholder="Enter prompt template here..."
                  spellCheck={false}
                />
              )}
              <p className="text-xs text-gray-500 mt-1">
                Use {'{variable_name}'} placeholders for dynamic data
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-4 border-t border-gray-200">
              <button
                onClick={handleSave}
                disabled={!hasChanges || saving || loading}
                className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors font-medium"
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
              
              <button
                onClick={handleReset}
                disabled={saving || loading}
                className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
              >
                Reset to Default
              </button>
              
              <button
                onClick={handleCancel}
                disabled={!hasChanges || saving || loading}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 disabled:bg-gray-200 disabled:cursor-not-allowed transition-colors"
              >
                Cancel Changes
              </button>
            </div>
          </div>

          {/* Variables Reference Panel */}
          <div className="w-80 border-l border-gray-200 bg-gray-50 p-4 overflow-auto">
            <h3 className="font-bold text-gray-900 mb-3 sticky top-0 bg-gray-50 pb-2">
              📚 Available Variables
            </h3>
            
            <div className="space-y-3">
              {variables.map((variable) => {
                const isUsed = usedVariables.includes(variable.variable_name);
                const isRequired = variable.is_required;
                
                return (
                  <div
                    key={variable.variable_name}
                    className={`p-3 rounded-md border-2 transition-all ${
                      isUsed
                        ? 'bg-green-50 border-green-300'
                        : isRequired
                        ? 'bg-yellow-50 border-yellow-300'
                        : 'bg-white border-gray-200'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-1">
                      <code className="text-xs font-mono font-bold text-purple-700 break-all">
                        {'{' + variable.variable_name + '}'}
                      </code>
                      <button
                        onClick={() => insertVariable(variable.variable_name)}
                        className="ml-2 px-2 py-1 text-xs bg-purple-100 text-purple-700 rounded hover:bg-purple-200 transition-colors flex-shrink-0"
                        title="Insert variable"
                      >
                        +
                      </button>
                    </div>
                    
                    <p className="text-xs text-gray-600 mb-1">
                      {variable.description}
                    </p>
                    
                    <div className="flex gap-2 items-center mt-2">
                      <span className="text-xs px-2 py-0.5 bg-gray-200 text-gray-700 rounded">
                        {variable.data_type}
                      </span>
                      {isRequired && (
                        <span className="text-xs px-2 py-0.5 bg-red-100 text-red-700 rounded font-medium">
                          Required
                        </span>
                      )}
                      {isUsed && (
                        <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded font-medium">
                          ✓ Used
                        </span>
                      )}
                    </div>
                    
                    {variable.example_value && (
                      <p className="text-xs text-gray-500 mt-1 italic">
                        Example: {variable.example_value}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SummaryPromptsEditor;
